import * as THREE from 'three';
import type { PaletteName } from '@/art/palette';
import { loadLevel, type LevelData } from '@/world/LevelLoader';
import type { CollisionWorld } from '@/world/collision';
import type { LightPool } from '@/fx/lighting';
import { CITY } from '@/world/grid';
import { levelDataFromBlueprint, type DistrictBlueprint } from '@/world/DistrictBlueprint';

/**
 * De Havenwijk — speelgebied van missie 1.
 *
 * "Smalle straten tussen hoge loodsen, kades met roestige kranen." Het level is
 * handgeplaatst als data: een kade langs een gracht aan de zuidkant, een straat erlangs, en
 * twee rijen loodsen die de smalle doorgangen vormen waar de missie zich afspeelt.
 *
 * Deze module is bewust dom: hij beschrijft wát er staat. De builders bepalen hóe het
 * eruitziet, de LevelLoader hangt collision en licht eraan.
 */

export interface BuildingPlacement {
  x: number;
  z: number;
  width: number;
  depth: number;
  floors: number;
  facing: 'north' | 'south' | 'east' | 'west';
  wall: PaletteName;
}

/**
 * Straat loopt langs X; de gracht ligt ten zuiden ervan.
 *
 * De kadelijn ligt op z = 22 en is de zuidgrens van de héle stad: elk district houdt
 * dezelfde grond-Z-extent (`DISTRICT_NORTH`..`QUAY_Z`) aan, zodat de chunks naadloos op
 * elkaar aansluiten en het kanaal als één doorlopende waterlijn leest. Zie
 * `docs/ARCHITECTURE.md`.
 *
 * Hij lag eerder op 14 terwijl het grondvlak tot z = 24 doorliep: tien meter asfalt op
 * straatniveau bóven water dat 1.2 m lager ligt, over de volle districtbreedte.
 */
const STREET_Z = 0;
export const QUAY_Z = 22;
export const DISTRICT_NORTH = -36;
const DISTRICT_WIDTH = 128;
/** Breedte van het vaarwater achter de kademuur. */
export const CANAL_DEPTH = 16;

/** Grondvlak dat overal in de stad hetzelfde is: van de achterrand tot exact de kademuur. */
export function cityGround(width: number, x: number): LevelData['ground'] {
  return {
    width,
    depth: QUAY_Z - DISTRICT_NORTH,
    x,
    z: (QUAY_Z + DISTRICT_NORTH) / 2,
    pavements: { north: true, south: true },
  };
}

export const HARBOR = {
  /** Waar Luca de missie begint: bij het restaurant aan de oostkant. */
  playerSpawn: new THREE.Vector2(32, STREET_Z),
  /** The safehouse entrance sits on the street, outside the warehouse collision block. */
  safehouse: new THREE.Vector2(-50, -4.2),
  streetZ: STREET_Z,
  quayZ: QUAY_Z,
  width: DISTRICT_WIDTH,
} as const;

/** Loodsen aan de noordkant van de straat, met hun gevels naar de straat toe. */
const NORTH_ROW: BuildingPlacement[] = [
  { x: -58, z: -16, width: 10, depth: 12, floors: 3, facing: 'south', wall: 'nightBrick' },
  { x: -47, z: -16.5, width: 10, depth: 13, floors: 2, facing: 'south', wall: 'oxideMaroon' },
  { x: -36, z: -16, width: 10, depth: 12, floors: 3, facing: 'south', wall: 'slateGreen' },
  { x: -25, z: -15.5, width: 10, depth: 11, floors: 2, facing: 'south', wall: 'brickRed' },
  { x: -14, z: -16.5, width: 10, depth: 13, floors: 3, facing: 'south', wall: 'nightBrick' },
  { x: -3, z: -16, width: 10, depth: 12, floors: 2, facing: 'south', wall: 'oxideMaroon' },
  { x: 8, z: -15.5, width: 10, depth: 11, floors: 3, facing: 'south', wall: 'brickRed' },
  { x: 19, z: -16.5, width: 10, depth: 13, floors: 2, facing: 'south', wall: 'nightBrick' },
  { x: 30, z: -16, width: 10, depth: 12, floors: 3, facing: 'south', wall: 'slateGreen' },
  { x: 41, z: -16.5, width: 10, depth: 13, floors: 2, facing: 'south', wall: 'oxideMaroon' },
  { x: 52, z: -15.5, width: 10, depth: 11, floors: 3, facing: 'south', wall: 'nightBrick' },
  { x: 61, z: -15.5, width: 7, depth: 11, floors: 2, facing: 'south', wall: 'brickRed' },
];

/** Achterwand boven de noordelijke straatwand: skyline/dichtheid zonder de kade te blokkeren. */
const BACK_ROW: BuildingPlacement[] = [
  { x: -58, z: -30, width: 10, depth: 6, floors: 3, facing: 'south', wall: 'nightBrick' },
  { x: -45, z: -30, width: 10, depth: 6, floors: 2, facing: 'south', wall: 'brickRed' },
  { x: -30, z: -30, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'slateGreen' },
  { x: -14, z: -30, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'nightBrick' },
  { x: 2, z: -30, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'oxideMaroon' },
  { x: 18, z: -30, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'brickRed' },
  { x: 34, z: -30, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'nightBrick' },
  { x: 50, z: -30, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'slateGreen' },
];

/** Lage kadesheds met brede gaten ertussen; de kade zelf blijft loopbaar. */
const SOUTH_ROW: BuildingPlacement[] = [
  { x: -58, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'nightBrick' },
  { x: -44, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'oxideMaroon' },
  { x: -24, z: 7.1, width: 10, depth: 4, floors: 1, facing: 'north', wall: 'slateGreen' },
  { x: -8, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'brickRed' },
  { x: 12, z: 7.1, width: 10, depth: 4, floors: 1, facing: 'north', wall: 'nightBrick' },
  { x: 32, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'oxideMaroon' },
  { x: 56, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'brickRed' },
];

/** Lantaarns langs de straat en de kade. */
const QUAY_WALK_Z = QUAY_Z - 2.5;
/** Kraanpad: de mast staat op de kade, de giek hangt over het water. */
const CRANE_Z = QUAY_Z - 4;

const LAMP_POSITIONS: Array<[number, number, number]> = [
  // [x, z, rotatie in radialen — bepaalt naar welke kant de arm wijst]
  [-58, -5.5, 0],
  [-42, -5.5, 0],
  [-26, -5.5, 0],
  [-10, -5.5, 0],
  [6, -5.5, 0],
  [22, -5.5, 0],
  [38, -5.5, 0],
  [54, -5.5, 0],
  [-54, QUAY_WALK_Z, Math.PI],
  [-34, QUAY_WALK_Z, Math.PI],
  [-14, QUAY_WALK_Z, Math.PI],
  [6, QUAY_WALK_Z, Math.PI],
  [26, QUAY_WALK_Z, Math.PI],
  [46, QUAY_WALK_Z, Math.PI],
  [60, QUAY_WALK_Z, Math.PI],
];

export const HARBOR_BLUEPRINT: DistrictBlueprint = {
  id: 'harbor',
  levelName: 'NA_District_Harbor',
  districtName: 'De Haven',
  primaryRoute: 'Two-lane quay road between warehouse rows and the canal.',
  landmarks: ['Rusted dock crane and moored canal workboat.'],
  ground: cityGround(DISTRICT_WIDTH, 0),
  quayWall: {
    length: DISTRICT_WIDTH,
    x: 0,
    z: QUAY_Z,
  },
  quayDeck: {
    length: DISTRICT_WIDTH,
    depth: CITY.quayDepth,
    x: 0,
    z: QUAY_Z - CITY.quayDepth / 2,
  },
  water: {
    width: DISTRICT_WIDTH,
    depth: CANAL_DEPTH,
    x: 0,
    z: QUAY_Z + CANAL_DEPTH / 2,
  },
  obstacles: [{ x: 0, z: QUAY_Z + 1, halfX: DISTRICT_WIDTH / 2, halfZ: 1, height: 2 }],
  // Geen bruggen: het kanaal is de zuidgrens van de wereld. De vorige brug spande 12 m over
  // 30 m water, eindigde in open water zonder overkant, had geen collision op het dek en
  // werd bovendien over de volle breedte afgesloten door de kademuur hierboven. Zodra er een
  // zuidoever is komt hij terug — `buildCanalBridge` staat klaar in de meshkit.
  bridges: [],
  roads: [
    {
      id: 'harbor-road-eastbound',
      x: -1,
      z: 1.1,
      halfX: 63,
      halfZ: 1.35,
      intent: 'Eastbound vehicle lane kept clear for driving.',
      route: {
        id: 'harbor-quay-eastbound',
        kind: 'sedan',
        points: [
          new THREE.Vector2(-62, 1.1),
          new THREE.Vector2(-34, 1.1),
          new THREE.Vector2(-4, 1.1),
          new THREE.Vector2(28, 1.1),
          new THREE.Vector2(62, 1.1),
        ],
        speedLimit: 3.2,
        loop: true,
      },
    },
    {
      id: 'harbor-road-westbound',
      x: 0,
      z: -2.9,
      halfX: 62,
      halfZ: 1.35,
      intent: 'Westbound vehicle lane kept clear for vans and chase cars.',
      route: {
        id: 'harbor-north-westbound',
        kind: 'van',
        points: [
          new THREE.Vector2(62, -2.9),
          new THREE.Vector2(34, -2.9),
          new THREE.Vector2(4, -2.9),
          new THREE.Vector2(-28, -2.9),
          new THREE.Vector2(-62, -2.9),
        ],
        speedLimit: 2.7,
        loop: true,
      },
    },
  ],
  pedestrianZones: [
    { id: 'harbor-north-sidewalk', kind: 'sidewalk', x: 0, z: -5.2, halfX: 63, halfZ: 1.45, intent: 'Warehouse office sidewalk with lamps, hydrants and phone booths.', allowedProps: ['phoneBooth', 'hydrant', 'trafficLight'] },
    { id: 'harbor-west-intersection', kind: 'intersection', x: -63, z: 2.9, halfX: 1.4, halfZ: 1.0, intent: 'Traffic control where the quay road enters the bridge seam.', allowedProps: ['trafficLight'] },
    { id: 'harbor-east-intersection', kind: 'intersection', x: 63, z: 2.9, halfX: 1.4, halfZ: 1.0, intent: 'Traffic control at the connector seam.', allowedProps: ['trafficLight'] },
    { id: 'harbor-quay-walk', kind: 'quay', x: 0, z: QUAY_WALK_Z, halfX: 63, halfZ: 2.2, intent: 'Quay edge pedestrian strip with benches and bollards.', allowedProps: ['bench', 'bollard'] },
    { id: 'harbor-loading-bays', kind: 'serviceYard', x: -1, z: 3.8, halfX: 61, halfZ: 1.2, intent: 'Loading bay pockets for crate cover beside warehouse doors.', allowedProps: ['crateStack'] },
    { id: 'harbor-west-crane-yard', kind: 'serviceYard', x: -46, z: CRANE_Z, halfX: 3.2, halfZ: 1.4, intent: 'Tall dock crane pad on the west pier edge.', allowedProps: ['crane'] },
    { id: 'harbor-east-crane-yard', kind: 'serviceYard', x: 56, z: CRANE_Z, halfX: 3.2, halfZ: 1.4, intent: 'Tall dock crane pad on the east pier edge.', allowedProps: ['crane'] },
    { id: 'harbor-parking-north', kind: 'parkingBay', x: -12, z: -3.7, halfX: 48, halfZ: 1.0, intent: 'Curbside parking on the north side of the road.', allowedVehicles: ['sedan', 'van', 'wreck'] },
    { id: 'harbor-water', kind: 'water', x: 0, z: QUAY_Z + CANAL_DEPTH / 2, halfX: 58, halfZ: CANAL_DEPTH / 2 - 1, intent: 'Canal water for boats and water contact props.', allowedProps: ['boat'] },
    { id: 'harbor-safehouse-approach', kind: 'objectiveArea', x: HARBOR.safehouse.x, z: HARBOR.safehouse.y, halfX: 3.4, halfZ: 1.2, intent: 'Mission-safe approach to the harbor safehouse.' },
  ],
  buildingBlocks: [
    {
      id: 'harbor-north-warehouse-row',
      intent: 'North warehouse wall: blocks the street edge and creates readable office-front sidewalks.',
      buildings: NORTH_ROW.map((placement, index) => ({ ...placement, seed: `harbor-${index}`, batch: 'north' })),
    },
    {
      id: 'harbor-back-industrial-wall',
      intent: 'Back-row warehouses add the dense skyline from the references without stealing playable sidewalk or quay space.',
      buildings: BACK_ROW.map((placement, index) => ({ ...placement, seed: `harbor-back-${index}`, batch: 'back' })),
    },
    {
      id: 'harbor-south-loading-row',
      intent: 'Sparse south sheds frame loading pockets but leave the quay reachable on foot.',
      buildings: SOUTH_ROW.map((placement, index) => ({ ...placement, seed: `harbor-${NORTH_ROW.length + index}`, batch: 'south' })),
    },
  ],
  streetlamps: LAMP_POSITIONS.map(([x, z, rotation]) => ({ x, z, rotation })),
  propClusters: [
    {
      id: 'harbor-sidewalk-fixtures',
      zoneId: 'harbor-north-sidewalk',
      intent: 'Office-front street furniture kept outside the vehicle lanes.',
      props: [
        { kind: 'phoneBooth', x: -39.5, z: -5.0, rotation: Math.PI },
        { kind: 'hydrant', x: -12, z: -4.9, rotation: 0 },
        { kind: 'hydrant', x: 24, z: -4.8, rotation: 0 },
        { kind: 'phoneBooth', x: 52, z: -5.0, rotation: Math.PI },
      ],
    },
    {
      id: 'harbor-intersection-controls',
      zoneId: 'harbor-east-intersection',
      intent: 'Traffic lights sit only on authored intersection corners.',
      props: [
        { kind: 'trafficLight', x: -63, z: 2.9, rotation: Math.PI / 2 },
        { kind: 'trafficLight', x: 63, z: 2.9, rotation: -Math.PI / 2 },
      ],
    },
    {
      id: 'harbor-loading-cover',
      zoneId: 'harbor-loading-bays',
      intent: 'Crates form cover where loading doors meet the quay road, never in the road lane.',
      // Alle kratten staan ten noorden van de loodsgevel (z 5.1) en ten zuiden van de
      // rijbaan (z 2.45). Ze zaten eerder deels in beide.
      props: [
        { kind: 'crateStack', x: -58, z: 3.9, rotation: 0.35 },
        { kind: 'crateStack', x: -49, z: 3.8, rotation: -0.55 },
        { kind: 'crateStack', x: -32, z: 4.0, rotation: 0.2 },
        { kind: 'crateStack', x: -25, z: 3.8, rotation: -0.2 },
        { kind: 'crateStack', x: -8, z: 3.9, rotation: 0.25 },
        { kind: 'crateStack', x: 13, z: 3.8, rotation: 0.55 },
        { kind: 'crateStack', x: 31, z: 4.1, rotation: -0.35 },
        { kind: 'crateStack', x: 50, z: 3.9, rotation: 0.15 },
        { kind: 'crateStack', x: 58, z: 4.0, rotation: -0.18 },
      ],
    },
    {
      id: 'harbor-quay-edge',
      zoneId: 'harbor-quay-walk',
      intent: 'Bollards and benches trace the water edge without blocking car routes.',
      props: [
        { kind: 'bench', x: -25, z: QUAY_WALK_Z, rotation: Math.PI },
        { kind: 'bench', x: 3, z: QUAY_WALK_Z + 0.1, rotation: Math.PI },
        { kind: 'bench', x: 38, z: QUAY_WALK_Z, rotation: Math.PI },
        { kind: 'bollard', x: -60, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: -40, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: -28, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: -16, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: -4, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 8, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 20, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 32, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 44, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 58, z: QUAY_Z - 0.8 },
      ],
    },
    {
      id: 'harbor-west-dock-landmark',
      zoneId: 'harbor-west-crane-yard',
      intent: 'The west crane makes the industrial quay readable from the game camera.',
      // De giek is 7.2 m lang en steekt na de kwartslag over de wereld-Z-as naar het water.
      // Met de mast op de kade blijft de achterkant ruim vrij van de loodsgevel op z 9.1.
      props: [
        { kind: 'crane', x: -46, z: CRANE_Z, rotation: -Math.PI / 2 },
      ],
    },
    {
      id: 'harbor-east-dock-landmark',
      zoneId: 'harbor-east-crane-yard',
      intent: 'The east crane frames the connector edge like the reference harbor board.',
      props: [
        { kind: 'crane', x: 56, z: CRANE_Z, rotation: -Math.PI / 2 },
      ],
    },
    {
      id: 'harbor-moored-boat',
      zoneId: 'harbor-water',
      intent: 'The moored canal workboat sits in authored water, parallel to the quay.',
      props: [
        { kind: 'boat', x: -26, z: QUAY_Z + 7.4, rotation: Math.PI / 2 },
        { kind: 'boat', x: 14, z: QUAY_Z + 7.4, rotation: Math.PI / 2 },
        { kind: 'boat', x: 49, z: QUAY_Z + 9.8, rotation: Math.PI / 2 },
      ],
    },
    {
      id: 'harbor-facade-signs',
      zoneId: 'harbor-north-sidewalk',
      intent: 'Small neon signs attach to warehouse facades as navigational accents.',
      props: [
        { kind: 'neonSign', x: -58, z: -10.08, rotation: Math.PI },
        { kind: 'neonSign', x: -36, z: -10.08, rotation: Math.PI },
        { kind: 'neonSign', x: -14, z: -10.08, rotation: Math.PI },
        { kind: 'neonSign', x: 8, z: -10.08, rotation: Math.PI },
        { kind: 'neonSign', x: 30, z: -10.08, rotation: Math.PI },
        { kind: 'neonSign', x: 52, z: -10.08, rotation: Math.PI },
      ],
    },
  ],
  parkedVehicles: [
    {
      id: 'harbor-parked-sedan-1',
      kind: 'sedan',
      position: new THREE.Vector2(8, -3.6),
      rotation: Math.PI / 2,
      stealable: true,
    },
    {
      id: 'harbor-parked-wreck-1',
      kind: 'wreck',
      position: new THREE.Vector2(-30, -3.7),
      rotation: -Math.PI / 2,
      stealable: false,
    },
    {
      id: 'harbor-parked-van-1',
      kind: 'van',
      position: new THREE.Vector2(34, -3.7),
      rotation: Math.PI / 2,
      stealable: false,
    },
  ],
  safehouse: {
    x: HARBOR.safehouse.x,
    z: HARBOR.safehouse.y,
  },
};

export const HARBOR_LEVEL: LevelData = levelDataFromBlueprint(HARBOR_BLUEPRINT);

export function buildHarbor(scene: THREE.Scene, collision: CollisionWorld, lights: LightPool): void {
  loadLevel(scene, collision, lights, HARBOR_LEVEL);
}
